import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Loader2, LogOut, Upload, Image as ImageIcon, LayoutGrid, Square, Home, X, Download, MessageSquare, Wrench, CheckCircle2, Bookmark, Trash2, Cuboid, Gift, Copy, Share2 } from "lucide-react";
import Seo from "@/components/Seo";
import PanoramaViewer from "@/components/PanoramaViewer";
import { useStudioAuth } from "@/context/StudioAuthContext";
import { studioApi, STUDIO_TOKEN_KEY } from "@/lib/studioApi";
import { formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { BRAND } from "@/lib/data";

const MODES = [
  { key: "floor_plan", label: "Floor Plan → 3D", icon: LayoutGrid, hint: "Upload a floor plan drawing. AI will visualise it in 3D." },
  { key: "wall", label: "Wall → Redesign", icon: Square, hint: "Upload a photo of a bare wall. AI will add a wardrobe / feature." },
  { key: "room", label: "Room → Makeover", icon: Home, hint: "Upload a photo of an existing room. AI will restyle it." },
  { key: "text", label: "Text → New Space", icon: Sparkles, hint: "No image needed — describe the space you want." },
];

const STYLES = ["Modern Minimal", "Warm Contemporary", "Luxe Classic", "Scandinavian", "Indian Contemporary", "Industrial"];

const MAX_MB = 5;

function readFileAsDataURL(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function StudioDashboard() {
  const { customer, ready, logout } = useStudioAuth();
  const nav = useNavigate();
  const location = useLocation();
  const googleExchanging = useRef(false);
  const [googleLoading, setGoogleLoading] = useState(
    typeof window !== "undefined" && (window.location.hash || "").includes("session_id=")
  );
  const [mode, setMode] = useState("wall");
  const [source, setSource] = useState(null); // data URL
  const [prompt, setPrompt] = useState("");
  const [roomType, setRoomType] = useState("");
  const [style, setStyle] = useState(STYLES[0]);
  const [wantPano, setWantPano] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [view, setView] = useState("2d");
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  // Google OAuth callback — exchange session_id from URL fragment ONCE.
  useEffect(() => {
    const hash = location.hash || "";
    if (!hash.includes("session_id=") || googleExchanging.current) return;
    googleExchanging.current = true;
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sid) { setGoogleLoading(false); return; }
    (async () => {
      try {
        const { data } = await studioApi.post("/studio/auth/google", { session_id: sid });
        localStorage.setItem(STUDIO_TOKEN_KEY, data.token);
        // Clean the hash so refresh doesn't retry
        window.history.replaceState(null, "", "/studio/dashboard");
        toast.success(`Welcome, ${data.customer?.name?.split(" ")[0] || "friend"}!`);
        window.location.reload(); // simplest way to re-init StudioAuthProvider with new token
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Google sign-in failed");
        window.history.replaceState(null, "", "/studio");
        nav("/studio", { replace: true });
      } finally {
        setGoogleLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (googleLoading) return; // still exchanging
    if (ready && !customer) nav("/studio", { replace: true });
  }, [ready, customer, nav, googleLoading]);

  useEffect(() => {
    if (!customer) return;
    studioApi.get("/studio/generations").then((r) => setHistory(r.data || [])).catch(() => {});
  }, [customer]);

  const [projects, setProjects] = useState([]);
  useEffect(() => {
    if (!customer) return;
    studioApi.get("/studio/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, [customer]);

  const [shortlist, setShortlist] = useState([]);
  const loadShortlist = useCallback(() => {
    if (!customer) return;
    studioApi.get("/studio/shortlist").then((r) => setShortlist(r.data || [])).catch(() => {});
  }, [customer]);
  useEffect(() => { loadShortlist(); }, [loadShortlist]);

  const [referral, setReferral] = useState(null);
  useEffect(() => {
    if (!customer) return;
    studioApi.get("/studio/refer").then((r) => setReferral(r.data)).catch(() => {});
  }, [customer]);

  const removeShortlist = async (id) => {
    if (!window.confirm("Remove this saved design?")) return;
    try {
      await studioApi.delete(`/studio/shortlist/${id}`);
      setShortlist((s) => s.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error("Couldn't remove. Please try again.");
    }
  };

  const onFile = async (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image must be under ${MAX_MB}MB.`);
      return;
    }
    try {
      const dataUrl = await readFileAsDataURL(f);
      setSource(dataUrl);
    } catch {
      toast.error("Couldn't read the image.");
    }
  };

  const currentMode = MODES.find((m) => m.key === mode);
  const needsUpload = mode !== "text";

  const generate = async () => {
    if (needsUpload && !source) {
      toast.error("Please upload an image for this mode.");
      return;
    }
    setLoading(true);
    setResult(null);
    setView("2d");
    try {
      const { data } = await studioApi.post("/studio/generate", {
        mode,
        prompt,
        source_image: needsUpload ? source : null,
        room_type: roomType || null,
        style,
        want_panorama: wantPano,
      });
      setResult(data);
      trackEvent("studio_generated", { mode, has_upload: !!source, want_panorama: wantPano });
      // Prepend to history for instant UX
      setHistory((h) => [
        { id: data.id, mode, image: data.image, panorama: data.panorama, created_at: data.created_at, prompt, style, room_type: roomType },
        ...h,
      ]);
      toast.success("Design ready!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403) {
        toast.error(formatApiError(detail) || "Your account has been paused. Please contact ZEBO.");
        logout();
        nav("/studio", { replace: true });
        return;
      }
      toast.error(formatApiError(detail) || "Generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const download = (dataUrl, name) => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = name || "zebo-studio.png";
    a.click();
  };

  const doLogout = () => {
    logout();
    nav("/studio", { replace: true });
  };

  if (googleLoading) {
    return (
      <main data-testid="studio-google-callback" className="min-h-screen bg-[#143D63] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="text-[#F8C21C] animate-spin mx-auto mb-5" />
          <p className="font-serif text-2xl text-[#F9F8F6] font-light">Signing you in with Google…</p>
        </div>
      </main>
    );
  }

  if (!ready || !customer) {
    return (
      <main className="min-h-screen bg-[#143D63] flex items-center justify-center">
        <Loader2 size={30} className="text-[#F8C21C] animate-spin" />
      </main>
    );
  }

  const chip = (active) =>
    `px-4 py-2 text-sm border transition-colors ${active ? "bg-[#143D63] text-[#F9F8F6] border-[#143D63]" : "bg-white text-[#143D63] border-[#143D63]/25 hover:border-[#143D63]"}`;

  return (
    <main data-testid="studio-dashboard-page" className="min-h-screen bg-[#F9F8F6]">
      <Seo title="ZEBO Studio — AI Interior Design Portal" description="Upload a floor plan, wall or room and let ZEBO's AI redesign it in seconds." path="/studio/dashboard" />

      {/* Top bar */}
      <header className="bg-[#143D63] text-[#F9F8F6] border-b border-[#F8C21C]/20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={BRAND.logo} alt={BRAND.full} className="h-12 w-auto object-contain bg-[#F9F8F6] px-2 py-1" />
            <div className="hidden sm:block">
              <p className="overline text-[#F8C21C] text-[10px]">ZEBO Studio</p>
              <p className="text-sm">Hi, {customer.name?.split(" ")[0] || customer.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-[#F9F8F6]/60">{customer.gen_count} generations</span>
            <button data-testid="studio-logout" onClick={doLogout} className="inline-flex items-center gap-2 border border-[#F9F8F6]/30 px-4 py-2 text-xs font-bold hover:bg-[#F9F8F6]/10">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      {customer.gen_count >= 5 && !customer.converted_to_lead && (
        <div data-testid="studio-nudge-banner" className="bg-[#F8C21C] text-[#143D63]">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-sm font-semibold flex items-center gap-2">
              <MessageSquare size={16} /> Loving what the AI made? You&apos;ve generated <b>{customer.gen_count}</b> designs. Let&apos;s bring one to life.
            </p>
            <button
              data-testid="studio-nudge-cta"
              onClick={() => {
                trackEvent("studio_nudge_click", { gen_count: customer.gen_count });
                nav(`/consultation?intent=studio&email=${encodeURIComponent(customer.email)}&name=${encodeURIComponent(customer.name || "")}`);
              }}
              className="inline-flex items-center gap-2 bg-[#143D63] text-[#F9F8F6] px-4 py-2 text-xs font-bold hover:bg-[#8B0000] transition-colors"
            >
              Book a Free Consultation
            </button>
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-12 gap-8">
        {projects.length > 0 && (
          <div data-testid="studio-projects" className="lg:col-span-12 mb-2">
            <p className="overline text-[#8B0000] mb-3 flex items-center gap-2"><Wrench size={13} /> Your active project{projects.length > 1 ? "s" : ""}</p>
            <div className="grid md:grid-cols-2 gap-4">
              {projects.map((p) => (
                <div key={p.id} data-testid={`project-card-${p.id}`} className="bg-white border border-[#143D63]/15 p-6">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <h3 className="font-serif text-xl text-[#143D63] leading-tight">{p.title}</h3>
                      {p.address && <p className="text-xs text-[#143D63]/60 mt-1">{p.address}</p>}
                    </div>
                    <span className="bg-[#F8C21C] text-[#143D63] text-[10px] font-bold uppercase tracking-wider px-2 py-1">{p.status}</span>
                  </div>
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-[#143D63]/70 mb-1"><span>Progress</span><span className="font-semibold">{p.progress_pct}%</span></div>
                    <div className="h-2 bg-[#143D63]/10 overflow-hidden"><div className="h-full bg-[#143D63]" style={{ width: `${p.progress_pct}%` }} /></div>
                  </div>
                  <div className="grid grid-cols-5 gap-1 mb-4">
                    {(p.milestones || []).map((m, i) => (
                      <div key={i} title={m.name} className={`h-1.5 ${m.done ? "bg-[#F8C21C]" : "bg-[#143D63]/10"}`} />
                    ))}
                  </div>
                  {p.updates && p.updates.length > 0 && (
                    <div className="border-t border-[#143D63]/10 pt-3">
                      <p className="text-[10px] uppercase tracking-wider text-[#143D63]/50 mb-2">Latest updates</p>
                      <ul className="space-y-2 max-h-28 overflow-y-auto">
                        {p.updates.slice(0, 3).map((u, i) => (
                          <li key={i} className="text-xs text-[#143D63] flex items-start gap-2">
                            <CheckCircle2 size={12} className="text-[#F8C21C] mt-0.5 shrink-0" />
                            <span className="flex-1">
                              {u.text}
                              <span className="block text-[#143D63]/50 text-[10px]">{new Date(u.at).toLocaleString()}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {referral?.code && (
          <div data-testid="studio-referral" className="lg:col-span-12 mb-2">
            <div className="grid lg:grid-cols-12 gap-4 bg-gradient-to-br from-[#143D63] to-[#0F2E4C] text-[#F9F8F6] p-6 sm:p-8">
              <div className="lg:col-span-5">
                <p className="overline text-[#F8C21C] mb-2 flex items-center gap-1.5"><Gift size={13} /> Refer &amp; Earn</p>
                <h3 className="font-serif text-3xl sm:text-4xl leading-tight">Send a friend, earn ₹5,000 credit.</h3>
                <p className="text-[#F9F8F6]/70 text-sm mt-3">
                  Share your code with anyone planning an interior project. When they book a consultation and sign a BOQ, we credit ₹5,000 towards your project (or on-site design services) — and they get an extra design consultation on us.
                </p>
              </div>
              <div className="lg:col-span-4">
                <p className="overline text-[#F8C21C]/70 mb-2">Your referral code</p>
                <div className="bg-white/10 border border-[#F8C21C]/40 p-4">
                  <p data-testid="referral-code" className="font-serif text-3xl text-[#F8C21C] tracking-widest">{referral.code}</p>
                  <p className="text-[11px] text-[#F9F8F6]/60 mt-1 break-all">{referral.share_url}</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    data-testid="referral-copy-btn"
                    onClick={async () => { await navigator.clipboard.writeText(referral.share_url); toast.success("Link copied — paste anywhere"); }}
                    className="inline-flex items-center gap-1.5 bg-[#F8C21C] text-[#143D63] px-3 py-2 text-[11px] font-bold hover:bg-[#F9F8F6] transition-colors"
                  >
                    <Copy size={12} /> Copy link
                  </button>
                  <a
                    data-testid="referral-whatsapp-btn"
                    href={`https://wa.me/?text=${encodeURIComponent(`I just finished my home with ZEBO — genuinely great team. If you're planning yours, use my code ${referral.code} at booking and you get an extra design consultation on the house: ${referral.share_url}`)}`}
                    target="_blank" rel="nofollow noopener noreferrer external"
                    className="inline-flex items-center gap-1.5 bg-[#25D366] text-white px-3 py-2 text-[11px] font-bold hover:opacity-90 transition-opacity"
                  >
                    <Share2 size={12} /> WhatsApp
                  </a>
                  <button
                    data-testid="referral-share-btn"
                    onClick={async () => {
                      if (navigator.share) {
                        try { await navigator.share({ title: "ZEBO Interior Design", text: `Use my code ${referral.code} at ZEBO`, url: referral.share_url }); } catch (_) {}
                      } else {
                        await navigator.clipboard.writeText(referral.share_url);
                        toast.success("Link copied");
                      }
                    }}
                    className="inline-flex items-center gap-1.5 bg-white/10 text-[#F9F8F6] border border-[#F9F8F6]/25 px-3 py-2 text-[11px] font-bold hover:bg-white/20 transition-colors"
                  >
                    <Share2 size={12} /> Share…
                  </button>
                </div>
              </div>
              <div className="lg:col-span-3">
                <p className="overline text-[#F8C21C]/70 mb-2">Your stats</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="border border-[#F8C21C]/25 p-3">
                    <p data-testid="referral-stat-leads" className="font-serif text-2xl text-[#F8C21C]">{referral.stats?.leads || 0}</p>
                    <p className="text-[10px] text-[#F9F8F6]/60 uppercase tracking-wider mt-1">Leads</p>
                  </div>
                  <div className="border border-[#F8C21C]/25 p-3">
                    <p data-testid="referral-stat-converted" className="font-serif text-2xl text-[#F8C21C]">{referral.stats?.converted || 0}</p>
                    <p className="text-[10px] text-[#F9F8F6]/60 uppercase tracking-wider mt-1">Signed</p>
                  </div>
                  <div className="border border-[#F8C21C]/25 p-3">
                    <p data-testid="referral-stat-credit" className="font-serif text-2xl text-[#F8C21C]">₹{((referral.stats?.credit_earned_inr || 0) / 1000).toFixed(0)}K</p>
                    <p className="text-[10px] text-[#F9F8F6]/60 uppercase tracking-wider mt-1">Earned</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {shortlist.length > 0 && (
          <div data-testid="studio-shortlist" className="lg:col-span-12 mb-2">
            <div className="flex items-center justify-between mb-3">
              <p className="overline text-[#8B0000] flex items-center gap-2"><Bookmark size={13} /> Your saved kitchen designs</p>
              <Link to="/configurator" className="text-xs text-[#143D63] font-bold hover:text-[#8B0000] inline-flex items-center gap-1"><Cuboid size={12} /> Design another</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {shortlist.map((s) => {
                const c = s.config || {};
                return (
                  <div key={s.id} data-testid={`shortlist-card-${s.id}`} className="bg-white border border-[#F8C21C]/40 p-5 group relative">
                    <button
                      onClick={() => removeShortlist(s.id)}
                      data-testid={`shortlist-remove-${s.id}`}
                      className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity text-[#8B0000] hover:bg-[#8B0000] hover:text-white p-1"
                      title="Remove"
                    >
                      <Trash2 size={13} />
                    </button>
                    <p className="font-serif text-lg text-[#143D63] leading-tight mb-1">{s.label}</p>
                    <p className="text-[11px] text-[#143D63]/50 mb-3">{new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                    <div className="space-y-1 text-xs text-[#143D63]/75">
                      <p><b>{c.length_ft}×{c.width_ft} ft</b> · {c.cabinets}</p>
                      <p>{c.countertop} · {c.flooring}</p>
                    </div>
                    <p className="mt-3 font-serif text-2xl text-[#8B0000]" data-testid={`shortlist-total-${s.id}`}>{c.estimated_total_inr}</p>
                    <Link
                      to="/configurator"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#143D63] link-underline"
                    >
                      Open in configurator →
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Controls */}
        <div className="lg:col-span-5">
          <div className="border border-[#143D63]/15 bg-white p-7 space-y-7">
            <div>
              <p className="overline text-[#143D63]/60 mb-3">Mode</p>
              <div className="grid grid-cols-2 gap-2">
                {MODES.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    data-testid={`studio-mode-${m.key}`}
                    onClick={() => { setMode(m.key); setSource(null); setResult(null); }}
                    className={`text-left border p-3 transition-colors ${mode === m.key ? "border-[#143D63] bg-[#143D63] text-[#F9F8F6]" : "border-[#143D63]/20 bg-white text-[#143D63] hover:border-[#143D63]"}`}
                  >
                    <m.icon size={16} className={mode === m.key ? "text-[#F8C21C]" : "text-[#8B0000]"} />
                    <p className="text-sm font-bold mt-2">{m.label}</p>
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#143D63]/60 mt-3">{currentMode?.hint}</p>
            </div>

            {needsUpload && (
              <div>
                <p className="overline text-[#143D63]/60 mb-3">Your image</p>
                <label
                  data-testid="studio-upload-label"
                  htmlFor="studio-upload"
                  className="border-2 border-dashed border-[#143D63]/25 aspect-video flex flex-col items-center justify-center cursor-pointer hover:border-[#F8C21C] transition-colors relative overflow-hidden bg-[#F9F8F6]"
                >
                  {source ? (
                    <>
                      <img src={source} alt="preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        data-testid="studio-clear-upload"
                        onClick={(e) => { e.preventDefault(); setSource(null); if (fileRef.current) fileRef.current.value = ""; }}
                        className="absolute top-2 right-2 bg-[#0A0A0A]/70 text-[#F9F8F6] p-1.5 hover:bg-[#8B0000]"
                        aria-label="Remove image"
                      >
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <div className="text-center px-6">
                      <Upload size={24} className="text-[#8B0000] mx-auto mb-2" />
                      <p className="text-sm text-[#143D63] font-semibold">Click to upload</p>
                      <p className="text-xs text-[#143D63]/60 mt-1">JPG / PNG up to {MAX_MB}MB</p>
                    </div>
                  )}
                </label>
                <input
                  ref={fileRef}
                  id="studio-upload"
                  data-testid="studio-upload-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
              </div>
            )}

            <div>
              <p className="overline text-[#143D63]/60 mb-3">Style</p>
              <div className="flex flex-wrap gap-2">
                {STYLES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStyle(s)}
                    data-testid={`studio-style-${s.toLowerCase().replace(/ /g, "-")}`}
                    className={chip(style === s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="overline text-[#143D63]/60 mb-3">Room / Element (optional)</p>
              <input
                data-testid="studio-roomtype"
                value={roomType}
                onChange={(e) => setRoomType(e.target.value)}
                placeholder="e.g. wardrobe with sliding doors, master bedroom, office reception"
                className="w-full border border-[#143D63]/20 bg-[#F9F8F6] px-4 py-3 text-sm text-[#143D63] focus:border-[#F8C21C] focus:outline-none"
              />
            </div>

            <div>
              <p className="overline text-[#143D63]/60 mb-3">Notes / prompt</p>
              <textarea
                data-testid="studio-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                placeholder={mode === "wall" ? "e.g. Add a floor-to-ceiling wardrobe with warm oak veneer and backlit glass shutters" : "Anything else the AI should know"}
                className="w-full border border-[#143D63]/20 bg-[#F9F8F6] px-4 py-3 text-sm text-[#143D63] focus:border-[#F8C21C] focus:outline-none resize-none"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-[#143D63]/80 select-none">
              <input
                data-testid="studio-want-pano"
                type="checkbox"
                checked={wantPano}
                onChange={(e) => setWantPano(e.target.checked)}
                className="accent-[#143D63]"
              />
              Also generate a 360° walkthrough (takes longer)
            </label>

            <button
              data-testid="studio-generate"
              onClick={generate}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#F8C21C] text-[#143D63] px-7 py-3.5 font-bold text-sm hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors disabled:opacity-60"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Rendering…</> : <><Sparkles size={16} /> Generate design</>}
            </button>
          </div>
        </div>

        {/* Result */}
        <div className="lg:col-span-7">
          <div className="border border-[#143D63]/15 bg-white p-3 sm:p-4">
            {result && result.panorama && (
              <div className="flex items-center gap-1 mb-3 border border-[#143D63]/15 bg-white w-fit">
                <button
                  data-testid="studio-view-2d"
                  onClick={() => setView("2d")}
                  className={`px-4 py-1.5 text-xs font-bold ${view === "2d" ? "bg-[#143D63] text-[#F9F8F6]" : "text-[#143D63]"}`}
                >
                  2D
                </button>
                <button
                  data-testid="studio-view-360"
                  onClick={() => { setView("360"); trackEvent("view_360_open", { event_category: "studio", mode }); }}
                  className={`px-4 py-1.5 text-xs font-bold ${view === "360" ? "bg-[#143D63] text-[#F9F8F6]" : "text-[#143D63]"}`}
                >
                  360°
                </button>
              </div>
            )}
            <div className="bg-[#143D63] aspect-[4/3] relative overflow-hidden flex items-center justify-center">
              <AnimatePresence mode="wait">
                {loading && (
                  <motion.div key="l" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center px-8">
                    <Loader2 size={36} className="animate-spin text-[#F8C21C] mx-auto mb-4" />
                    <p className="font-serif text-2xl text-[#F9F8F6] font-light">Rendering your design…</p>
                    <p className="text-[#F9F8F6]/60 text-sm mt-2">{wantPano ? "2D + 360° take ~25–45s." : "Should take ~15–25s."}</p>
                  </motion.div>
                )}
                {!loading && result && view === "2d" && (
                  <motion.img key="i" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} src={result.image} alt="generated" className="w-full h-full object-cover" />
                )}
                {!loading && result && view === "360" && result.panorama && (
                  <motion.div key="p" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full h-full">
                    <PanoramaViewer src={result.panorama} />
                  </motion.div>
                )}
                {!loading && !result && (
                  <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center px-8">
                    <ImageIcon size={36} className="text-[#F8C21C] mx-auto mb-4" />
                    <p className="font-serif text-2xl text-[#F9F8F6] font-light">Your design will appear here</p>
                    <p className="text-[#F9F8F6]/60 text-sm mt-2">Configure the panel and hit Generate.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {result && (
              <div className="flex justify-end mt-3">
                <button
                  data-testid="studio-download"
                  onClick={() => download(view === "360" ? result.panorama : result.image, `zebo-studio-${mode}-${Date.now()}.png`)}
                  className="inline-flex items-center gap-2 border border-[#143D63] text-[#143D63] px-4 py-2 text-xs font-bold hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors"
                >
                  <Download size={13} /> Download {view === "360" ? "panorama" : "image"}
                </button>
              </div>
            )}
          </div>

          {/* History */}
          <div className="mt-8">
            <p className="overline text-[#143D63]/60 mb-4">Your generations ({history.length})</p>
            {history.length === 0 ? (
              <p className="text-sm text-[#143D63]/60 border border-dashed border-[#143D63]/20 p-6 text-center">Nothing yet — your generations will appear here.</p>
            ) : (
              <div data-testid="studio-history" className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {history.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => { setResult({ id: h.id, image: h.image, panorama: h.panorama }); setView("2d"); }}
                    className="group relative aspect-square overflow-hidden border border-[#143D63]/15 bg-[#F9F8F6]"
                  >
                    <img src={h.image} alt={h.mode} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    <span className="absolute bottom-1 left-1 bg-[#0A0A0A]/70 text-[#F9F8F6] text-[10px] px-2 py-0.5">{h.mode}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
